import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Button, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';

import {
  setEventsCategoryFilter,
  setEventsTimelineFilter,
  setEventsFavoritesFilter,
} from 'modules/events/store/eventsActions';
import { EventCategory } from 'modules/events/types/eventsTypes';
import { eventsFilters } from 'modules/events/constants/eventsFilters';

import { getEventsFilters } from '../../selectors/eventsSelectors';

const useStyle = makeStyles(theme => ({
  button: {
    height: 45,
    padding: '0 15px',
    marginRight: 10,
    fontSize: '0.875rem',
  },
  formControl: {
    marginTop: 0,
    marginRight: theme.spacing(1),
    minWidth: 120,

    '& fieldset': {
      top: 0,
    },

    '& legend': {
      display: 'none',
    },
  },
  label: {
    top: -16,
    marginLeft: 15,
    textTransform: 'uppercase',
    fontWeight: 500,
    fontSize: '0.8125rem',
  },
  container: {
    position: 'relative',
    marginBottom: theme.spacing(2),
  },
  favorite: {
    height: 45,
    padding: '0 15px',
    fontSize: '0.875rem',
  },
}));

const getCategoryFilter = (value: string): EventCategory | '' => {
  if (value === 'CREDIT_ANALYSIS' || value === 'ESG_ANALYSIS' || value === 'FUNDS_ANALYSIS')
    return value as EventCategory;

  return '';
};

const EventsFilters: FC = () => {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();
  const filters = useSelector(getEventsFilters);

  const handleCategoryFilterChange = (value: string) => () => {
    const category = getCategoryFilter(value);
    dispatch(setEventsCategoryFilter(category === filters.category ? '' : category));
  };

  const handleTimelineFilterChange = (value: string) => () =>
    dispatch(setEventsTimelineFilter(value === filters.timeline ? '' : value));

  const handleFavoritesFilterChange = () => dispatch(setEventsFavoritesFilter(!filters.favourites));

  return <>
    <Box className={classes.container}>
      <Grid container justifyContent="space-between">
        <Grid item key="categories">
          <Button
            key="all"
            variant={filters.category ? 'outlined' : 'contained'}
            color="primary"
            className={classes.button}
            size="small"
            onClick={handleCategoryFilterChange('')}
          >
            {t('All Events')}
          </Button>
          {Object.keys(eventsFilters.category).map(name => (
            <Button
              key={name}
              variant={filters.category === name ? 'contained' : 'outlined'}
              color="primary"
              className={classes.button}
              size="small"
              onClick={handleCategoryFilterChange(name as EventCategory)}
            >
              {t(eventsFilters.category[name as keyof typeof eventsFilters.category])}
            </Button>
          ))}
        </Grid>
        <Grid item>
          {Object.keys(eventsFilters.timeline).map(name => (
            <Button
              key={name}
              variant={filters.timeline === name ? 'contained' : 'outlined'}
              color="primary"
              className={classes.button}
              onClick={handleTimelineFilterChange(name)}
            >
              {t(eventsFilters.timeline[name as keyof typeof eventsFilters.timeline])}
            </Button>
          ))}
          <Button
            variant={filters.favourites ? 'contained' : 'outlined'}
            color="primary"
            className={classes.favorite}
            onClick={handleFavoritesFilterChange}
          >
            {t(eventsFilters.favourites)}
          </Button>
        </Grid>
      </Grid>
    </Box>
  </>;
};

export { EventsFilters };
